<template>
  <div v-if="visible" class="gmail-compose" :class="{
    'gmail-compose--max': isMaximized && !isMinimized && !isFullscreen,
    'gmail-compose--min': isMinimized,
    'gmail-compose--fullscreen': isFullscreen && !isMinimized
  }">
    <div class="gmail-compose__header" :class="{ 'gmail-compose__header--clickable': isMinimized }"
      @click="onHeaderClick">
      <span class="gmail-compose__title" :title="composeHeaderTitle">{{ composeHeaderTitle }}</span>
      <div class="gmail-compose__header-actions" @click.stop>
        <button type="button" class="gmail-compose__icon-btn" :title="isMinimized ? 'Maximize' : 'Minimize'"
          @click="toggleMinimize">
          <font-awesome-icon :icon="['fas', 'minus']" />
        </button>
        <button type="button" class="gmail-compose__icon-btn" :title="isMaximized ? 'Restore' : 'Expand'"
          @click="toggleMaximize">
          <font-awesome-icon :icon="['fas', isMaximized ? 'chevron-down' : 'chevron-up']" />
        </button>
        <button type="button" class="gmail-compose__icon-btn" :title="isFullscreen ? 'Exit full screen' : 'Full screen'"
          @click="toggleFullscreen">
          <font-awesome-icon :icon="['fas', isFullscreen ? 'compress' : 'expand']" />
        </button>
        <button type="button" class="gmail-compose__icon-btn" title="Close" @click="closeCompose">
          <font-awesome-icon :icon="['fas', 'times']" />
        </button>
      </div>
    </div>

    <div v-show="!isMinimized" class="gmail-compose__body">
      <div class="gmail-compose__recipients-wrap">
        <div class="gmail-compose__recipients-scroll">
          <div class="gmail-compose__row gmail-compose__row--to" :class="{
            'gmail-compose__row--to-collapsed': !isRecipientsExpanded,
            'gmail-compose__row--divider': ccBccEnabled && (showCc || showBcc)
          }">
            <button v-show="isRecipientsExpanded" type="button"
              class="gmail-compose__field-label gmail-compose__field-label--clickable"
              @click="openPicker('to')">To</button>
            <div class="gmail-compose__chips-wrap" @click="focusToInput">
              <span v-for="(r, idx) in toRecipients" :key="'to-' + idx + '-' + r.email" class="gmail-compose__chip">
                {{ chipLabel(r) }}
                <button type="button" class="gmail-compose__chip-remove"
                  @click.stop="removeRecipient('to', idx)">&times;</button>
              </span>
              <input ref="toInput" v-model="toInputText" type="text" class="gmail-compose__chip-input"
                :placeholder="showRecipientsPlaceholder ? 'Recipients' : ''" @focus="onToInputFocus"
                @input="onRecipientInput('to')" @keydown="onRecipientKeydown($event, 'to')"
                @blur="onRecipientBlur('to')" />
            </div>
            <span v-if="ccBccEnabled" v-show="showToRowCcBccLinks" class="gmail-compose__cc-bcc-links">
              <button type="button" class="gmail-compose__link-btn" @mousedown.prevent="onCcLinkClick">Cc</button>
              <button type="button" class="gmail-compose__link-btn" @mousedown.prevent="onBccLinkClick">Bcc</button>
            </span>
          </div>

          <div v-if="ccBccEnabled" v-show="showCc" class="gmail-compose__row gmail-compose__row--cc"
            :class="{ 'gmail-compose__row--divider': showBcc }">
            <button type="button" class="gmail-compose__field-label gmail-compose__field-label--clickable"
              @click="openPicker('cc')">Cc</button>
            <div class="gmail-compose__chips-wrap">
              <span v-for="(r, idx) in ccRecipients" :key="'cc-' + idx + '-' + r.email" class="gmail-compose__chip">
                {{ chipLabel(r) }}
                <button type="button" class="gmail-compose__chip-remove"
                  @click="removeRecipient('cc', idx)">&times;</button>
              </span>
              <input ref="ccInput" v-model="ccInputText" type="text" class="gmail-compose__chip-input"
                @focus="onCcInputFocus" @input="onRecipientInput('cc')" @keydown="onRecipientKeydown($event, 'cc')"
                @blur="onRecipientBlur('cc')" />
            </div>
            <span v-show="showCcRowBccLink" class="gmail-compose__cc-bcc-links">
              <button type="button" class="gmail-compose__link-btn" @mousedown.prevent="onBccLinkClick">Bcc</button>
            </span>
          </div>

          <div v-if="ccBccEnabled" v-show="showBcc" class="gmail-compose__row gmail-compose__row--bcc">
            <button type="button" class="gmail-compose__field-label gmail-compose__field-label--clickable"
              @click="openPicker('bcc')">Bcc</button>
            <div class="gmail-compose__chips-wrap">
              <span v-for="(r, idx) in bccRecipients" :key="'bcc-' + idx + '-' + r.email" class="gmail-compose__chip">
                {{ chipLabel(r) }}
                <button type="button" class="gmail-compose__chip-remove"
                  @click="removeRecipient('bcc', idx)">&times;</button>
              </span>
              <input ref="bccInput" v-model="bccInputText" type="text" class="gmail-compose__chip-input"
                @focus="onBccInputFocus" @input="onRecipientInput('bcc')" @keydown="onRecipientKeydown($event, 'bcc')"
                @blur="onRecipientBlur('bcc')" />
            </div>
            <span v-show="showBccRowCcLink" class="gmail-compose__cc-bcc-links">
              <button type="button" class="gmail-compose__link-btn" @mousedown.prevent="onCcLinkClick">Cc</button>
            </span>
          </div>
        </div>

        <ul v-if="showSuggestions" class="gmail-compose__suggestions">
          <li v-for="s in suggestions" :key="s.id + '-' + s.email" class="gmail-compose__suggestion"
            @mousedown.prevent="pickSuggestion(s)">
            <span class="gmail-compose__suggestion-name">{{ s.name }}</span>
            <span class="gmail-compose__suggestion-email">{{ s.email }}</span>
          </li>
        </ul>
      </div>

      <div class="gmail-compose__row gmail-compose__row--subject">
        <input ref="subjectInput" v-model="subject" type="text" class="gmail-compose__subject" placeholder="Subject" />
      </div>

      <div class="gmail-compose__editor">
        <QuillEditor v-model:content="bodyHtml" contentType="html" :options="editorOptions" theme="snow" />
      </div>

      <div class="gmail-compose__footer">
        <div ref="sendButtonWrap" class="gmail-compose__send-wrap">
          <button type="button" class="gmail-compose__send" :disabled="isSending" @click="sendEmail">
            <span>{{ sendButtonLabel }}</span>
          </button>
          <button type="button" class="gmail-compose__send-dropdown"
            :class="{ 'gmail-compose__send-dropdown--open': scheduleMenuVisible }" :disabled="isSending"
            :title="scheduleMenuVisible ? 'Close schedule options' : 'Schedule send'"
            :aria-expanded="scheduleMenuVisible ? 'true' : 'false'" @click.stop="toggleScheduleMenu">
            <font-awesome-icon :icon="['fas', 'chevron-down']" class="gmail-compose__send-chevron"
              :class="{ 'gmail-compose__send-chevron--open': scheduleMenuVisible }" />
          </button>
        </div>
        <button type="button" class="gmail-compose__discard" title="Discard" @click="closeCompose">
          <font-awesome-icon :icon="['fas', 'trash']" />
        </button>
      </div>
    </div>

    <ScheduleSendModals :visible="scheduleMenuVisible" :anchor-rect="sendButtonRect" @close="closeScheduleMenu"
      @schedule="onScheduleSelected" />

    <ContactPickerModal :visible="pickerVisible" :contacts="allContacts" :is-loading="contactsLoading"
      :initial-selected="pickerInitialSelected" @close="pickerVisible = false" @insert="onPickerInsert"
      @search="onPickerSearch" />
  </div>
</template>

<script>
import gql from "graphql-tag";
import { QuillEditor } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import ContactPickerModal from "./ContactPickerModal.vue";
import ScheduleSendModals from "./ScheduleSendModals.vue";

export default {
  name: "GmailCompose",
  components: {
    QuillEditor,
    ContactPickerModal,
    ScheduleSendModals,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    composeDraft: {
      type: Object,
      default: null,
    },
    listFilters: {
      type: Object,
      default: function () {
        return {};
      },
    },
    applyListFilters: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["close", "sent"],
  data: function () {
    return {
      isMinimized: false,
      isMaximized: true,
      isFullscreen: false,
      ccBccEnabled: false,
      showRecipientFields: false,
      showCc: false,
      showBcc: false,
      toRecipients: [],
      ccRecipients: [],
      bccRecipients: [],
      toInputText: "",
      ccInputText: "",
      bccInputText: "",
      activeField: null,
      subject: "",
      bodyHtml: "",
      isSending: false,
      allContacts: [],
      contactsLoading: false,
      pickerVisible: false,
      pickerTargetField: "to",
      pickerInitialSelected: [],
      scheduleMenuVisible: false,
      sendButtonRect: null,
      recipientSearchDebounceTimer: null,
    };
  },
  computed: {
    sendButtonLabel: function () {
      if (this.isSending) {
        return "Sending…";
      }
      return "Send";
    },
    composeHeaderTitle: function () {
      var text = (this.subject || "").trim();
      return text ? text : "New Message";
    },
    editorOptions: function () {
      return {
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ color: [] }, { background: [] }],
            [{ align: [] }],
            ["link"],
            ["clean"],
          ],
        },
        placeholder: "Write your message…",
      };
    },
    isRecipientsExpanded: function () {
      if (this.ccBccEnabled && (this.showCc || this.showBcc)) {
        return true;
      }
      return this.showRecipientFields;
    },
    showRecipientsPlaceholder: function () {
      var hasAny =
        this.toRecipients.length > 0 ||
        this.ccRecipients.length > 0 ||
        this.bccRecipients.length > 0;
      if (hasAny) {
        return false;
      }
      return !this.isRecipientsExpanded;
    },
    showToRowCcBccLinks: function () {
      return this.ccBccEnabled && this.isRecipientsExpanded && !this.showCc && !this.showBcc;
    },
    showCcRowBccLink: function () {
      return this.ccBccEnabled && this.showCc && !this.showBcc;
    },
    showBccRowCcLink: function () {
      return this.ccBccEnabled && this.showBcc && !this.showCc;
    },
    suggestions: function () {
      var field = this.activeField;
      if (!field) {
        return [];
      }
      var text = "";
      if (field === "to") {
        text = this.toInputText;
      } else if (field === "cc") {
        text = this.ccInputText;
      } else if (field === "bcc") {
        text = this.bccInputText;
      }
      var q = (text || "").trim().toLowerCase();
      if (q.length < 1) {
        return [];
      }
      var self = this;
      var list = this.allContacts || [];
      var out = [];
      for (var i = 0; i < list.length && out.length < 8; i++) {
        var c = list[i];
        if (self.isEmailUsedInField(c.email, field)) {
          continue;
        }
        var name = c.name ? c.name.toLowerCase() : "";
        var email = c.email ? c.email.toLowerCase() : "";
        if (name.indexOf(q) !== -1 || email.indexOf(q) !== -1) {
          out.push(c);
        }
      }
      return out;
    },
    showSuggestions: function () {
      return this.activeField && this.suggestions.length > 0;
    },
  },
  watch: {
    visible: function (val) {
      if (val) {
        var self = this;
        this.loadContacts().then(function () {
          self.$nextTick(function () {
            if (self.composeDraft) {
              self.applyComposeDraft(self.composeDraft);
              self.focusAfterDraftLoad();
            } else {
              self.focusRecipientsOnOpen();
            }
          });
        });
      }
    },
    isMinimized: function (val) {
      if (!val && this.visible) {
        var self = this;
        this.$nextTick(function () {
          self.focusRecipientsOnOpen();
        });
      }
    },
    listFilters: {
      deep: true,
      handler: function () {
        if (this.visible && this.applyListFilters) {
          this.loadContacts(this.getActiveRecipientSearchText());
        }
      },
    },
    applyListFilters: function () {
      if (this.visible) {
        this.loadContacts(this.getActiveRecipientSearchText());
      }
    },
  },
  beforeUnmount: function () {
    if (this.recipientSearchDebounceTimer) {
      clearTimeout(this.recipientSearchDebounceTimer);
    }
  },
  methods: {
    chipLabel: function (r) {
      if (r.name && r.email) {
        return r.name + " <" + r.email + ">";
      }
      return r.email || r.name || "";
    },
    focusToInput: function () {
      if (this.$refs.toInput) {
        this.$refs.toInput.focus();
      }
    },
    focusRecipientsOnOpen: function () {
      if (this.isMinimized) {
        return;
      }
      this.showRecipientFields = true;
      this.setActiveField("to");
      var self = this;
      this.$nextTick(function () {
        if (self.$refs.toInput) {
          self.$refs.toInput.focus();
        }
      });
    },
    onToInputFocus: function () {
      this.setActiveField("to");
    },
    onCcInputFocus: function () {
      this.setActiveField("cc");
    },
    onBccInputFocus: function () {
      this.setActiveField("bcc");
    },
    onRecipientInput: function (field) {
      this.activeField = field;
      this.showRecipientFields = true;
      this.scheduleRecipientContactSearch(field);
    },
    getActiveRecipientSearchText: function () {
      var field = this.activeField;
      if (!field) {
        return null;
      }
      var text = "";
      if (field === "to") {
        text = this.toInputText;
      } else if (field === "cc") {
        text = this.ccInputText;
      } else if (field === "bcc") {
        text = this.bccInputText;
      }
      text = (text || "").trim();
      return text ? text : null;
    },
    scheduleRecipientContactSearch: function (field) {
      var self = this;
      if (this.recipientSearchDebounceTimer) {
        clearTimeout(this.recipientSearchDebounceTimer);
      }
      this.recipientSearchDebounceTimer = setTimeout(function () {
        var searchText = null;
        if (field === "to") {
          searchText = self.toInputText ? self.toInputText.trim() : null;
        } else if (field === "cc") {
          searchText = self.ccInputText ? self.ccInputText.trim() : null;
        } else if (field === "bcc") {
          searchText = self.bccInputText ? self.bccInputText.trim() : null;
        }
        self.loadContacts(searchText || null);
      }, 300);
    },
    buildContactFilterVariables: function () {
      var variables = {};
      if (!this.applyListFilters) {
        return variables;
      }
      var filters = this.listFilters;
      if (!filters) {
        return variables;
      }
      if (filters.entities && filters.entities.length > 0) {
        variables.entities = filters.entities.slice();
      }
      if (filters.partner_type) {
        variables.partner_type = filters.partner_type;
      }
      if (filters.partner_ids && filters.partner_ids.length > 0) {
        variables.partner_ids = filters.partner_ids.slice();
      }
      return variables;
    },
    onCcLinkClick: function () {
      this.showRecipientFields = true;
      this.showCc = true;
      var self = this;
      this.$nextTick(function () {
        if (self.$refs.ccInput) {
          self.$refs.ccInput.focus();
        }
      });
    },
    onBccLinkClick: function () {
      this.showRecipientFields = true;
      this.showBcc = true;
      var self = this;
      this.$nextTick(function () {
        if (self.$refs.bccInput) {
          self.$refs.bccInput.focus();
        }
      });
    },
    syncRecipientFieldsCollapse: function () {
      if (this.pickerVisible) {
        return;
      }
      var hasTo = this.toRecipients.length > 0;
      var hasCc = this.ccRecipients.length > 0;
      var hasBcc = this.bccRecipients.length > 0;
      if (hasTo || hasCc || hasBcc) {
        this.showRecipientFields = true;
        if (this.ccBccEnabled && hasCc) {
          this.showCc = true;
        }
        if (this.ccBccEnabled && hasBcc) {
          this.showBcc = true;
        }
        return;
      }
      if (this.activeField) {
        return;
      }
      this.showRecipientFields = false;
      this.showCc = false;
      this.showBcc = false;
    },
    onHeaderClick: function () {
      if (this.isMinimized) {
        this.isMinimized = false;
      }
    },
    toggleMinimize: function () {
      this.isMinimized = !this.isMinimized;
    },
    toggleMaximize: function () {
      if (this.isFullscreen) {
        this.isFullscreen = false;
      }
      this.isMaximized = !this.isMaximized;
      if (this.isMaximized) {
        this.isMinimized = false;
      }
    },
    toggleFullscreen: function () {
      if (this.isFullscreen) {
        this.isFullscreen = false;
        return;
      }
      this.isFullscreen = true;
      this.isMinimized = false;
    },
    closeCompose: function () {
      this.closeScheduleMenu();
      this.resetForm();
      this.$emit("close");
    },
    toggleScheduleMenu: function () {
      if (this.scheduleMenuVisible) {
        this.closeScheduleMenu();
        return;
      }
      this.updateSendButtonRect();
      this.scheduleMenuVisible = true;
    },
    closeScheduleMenu: function () {
      this.scheduleMenuVisible = false;
    },
    updateSendButtonRect: function () {
      var el = this.$refs.sendButtonWrap;
      if (el && el.getBoundingClientRect) {
        this.sendButtonRect = el.getBoundingClientRect();
      } else {
        this.sendButtonRect = null;
      }
    },
    onScheduleSelected: function (scheduledAt) {
      this.submitEmail(scheduledAt);
    },
    formatScheduledAtForGraphql: function (value) {
      if (!value) {
        return null;
      }
      if (typeof value === "string" && value.indexOf("T") === -1 && value.indexOf(" ") !== -1) {
        return value;
      }
      var d = value instanceof Date ? value : new Date(value);
      if (isNaN(d.getTime())) {
        return value;
      }
      var y = d.getFullYear();
      var m = String(d.getMonth() + 1);
      if (m.length < 2) {
        m = "0" + m;
      }
      var day = String(d.getDate());
      if (day.length < 2) {
        day = "0" + day;
      }
      var h = String(d.getHours());
      if (h.length < 2) {
        h = "0" + h;
      }
      var min = String(d.getMinutes());
      if (min.length < 2) {
        min = "0" + min;
      }
      var sec = String(d.getSeconds());
      if (sec.length < 2) {
        sec = "0" + sec;
      }
      return y + "-" + m + "-" + day + " " + h + ":" + min + ":" + sec;
    },
    resetForm: function () {
      this.isMinimized = false;
      this.isMaximized = true;
      this.isFullscreen = false;
      this.showRecipientFields = false;
      this.showCc = false;
      this.showBcc = false;
      this.toRecipients = [];
      this.ccRecipients = [];
      this.bccRecipients = [];
      this.toInputText = "";
      this.ccInputText = "";
      this.bccInputText = "";
      this.activeField = null;
      this.subject = "";
      this.bodyHtml = "";
      this.pickerVisible = false;
    },
    applyComposeDraft: function (draft) {
      this.resetForm();
      if (!draft) {
        return;
      }
      this.subject = draft.subject ? draft.subject : "";
      this.bodyHtml = draft.bodyHtml ? draft.bodyHtml : "";
      var fields = ["to", "cc", "bcc"];
      for (var f = 0; f < fields.length; f++) {
        var field = fields[f];
        var list = draft[field];
        if (!list || !list.length) {
          continue;
        }
        for (var i = 0; i < list.length; i++) {
          var entry = list[i];
          this.matchContactForRecipient(entry);
          this.addRecipient(field, entry);
        }
      }
      this.showRecipientFields = true;
      if (this.ccRecipients.length > 0) {
        this.showCc = true;
      }
      if (this.bccRecipients.length > 0) {
        this.showBcc = true;
      }
      this.isMaximized = true;
      this.isMinimized = false;
      this.isFullscreen = false;
    },
    matchContactForRecipient: function (recipient) {
      if (!recipient || !recipient.email) {
        return;
      }
      var key = recipient.email.trim().toLowerCase();
      var contacts = this.allContacts || [];
      for (var i = 0; i < contacts.length; i++) {
        var contact = contacts[i];
        if (!contact || !contact.email) {
          continue;
        }
        if (contact.email.trim().toLowerCase() === key) {
          recipient.id = contact.id || null;
          if (!recipient.name && contact.name) {
            recipient.name = contact.name;
          }
          return;
        }
      }
    },
    focusAfterDraftLoad: function () {
      var hasRecipients =
        this.toRecipients.length > 0 ||
        this.ccRecipients.length > 0 ||
        this.bccRecipients.length > 0;
      var self = this;
      this.$nextTick(function () {
        if (hasRecipients && self.$refs.subjectInput) {
          self.$refs.subjectInput.focus();
          return;
        }
        self.focusRecipientsOnOpen();
      });
    },
    setActiveField: function (field) {
      this.activeField = field;
      this.showRecipientFields = true;
      if (this.ccBccEnabled && field === "cc") {
        this.showCc = true;
      }
      if (this.ccBccEnabled && field === "bcc") {
        this.showBcc = true;
      }
    },
    getRecipientsByField: function (field) {
      if (field === "cc") {
        return this.ccRecipients;
      }
      if (field === "bcc") {
        return this.bccRecipients;
      }
      return this.toRecipients;
    },
    isEmailUsedInField: function (email, field) {
      var key = (email || "").toLowerCase();
      var list = this.getRecipientsByField(field);
      for (var i = 0; i < list.length; i++) {
        if ((list[i].email || "").toLowerCase() === key) {
          return true;
        }
      }
      return false;
    },
    isValidEmail: function (email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },
    addRecipient: function (field, recipient) {
      if (!recipient || !recipient.email) {
        return;
      }
      var email = recipient.email.trim().toLowerCase();
      if (!this.isValidEmail(email)) {
        return;
      }
      if (this.isEmailUsedInField(email, field)) {
        return;
      }
      var entry = {
        email: email,
        name: recipient.name ? recipient.name.trim() : "",
        id: recipient.id || null,
      };
      var list = this.getRecipientsByField(field);
      list.push(entry);
    },
    removeRecipient: function (field, index) {
      var list = this.getRecipientsByField(field);
      list.splice(index, 1);
      this.syncRecipientFieldsCollapse();
    },
    commitInputText: function (field) {
      var text = "";
      if (field === "to") {
        text = this.toInputText;
        this.toInputText = "";
      } else if (field === "cc") {
        text = this.ccInputText;
        this.ccInputText = "";
      } else if (field === "bcc") {
        text = this.bccInputText;
        this.bccInputText = "";
      }
      text = (text || "").trim();
      if (!text) {
        return;
      }
      var parts = text.split(/[,;]+/);
      for (var i = 0; i < parts.length; i++) {
        var part = parts[i].trim();
        if (!part) {
          continue;
        }
        var name = "";
        var email = part;
        var match = part.match(/^(.+)<([^>]+)>$/);
        if (match) {
          name = match[1].trim();
          email = match[2].trim();
        }
        this.addRecipient(field, { email: email, name: name });
      }
    },
    onRecipientBlur: function (field) {
      var self = this;
      setTimeout(function () {
        self.commitInputText(field);
        self.activeField = null;
        self.syncRecipientFieldsCollapse();
      }, 150);
    },
    onRecipientKeydown: function (e, field) {
      if (e.key === "Enter" || e.key === "," || e.key === ";") {
        e.preventDefault();
        this.commitInputText(field);
        return;
      }
      if (e.key === "Backspace") {
        var text = field === "to" ? this.toInputText : field === "cc" ? this.ccInputText : this.bccInputText;
        if (!text) {
          var list = this.getRecipientsByField(field);
          if (list.length > 0) {
            list.pop();
          }
        }
      }
    },
    pickSuggestion: function (contact) {
      var field = this.activeField;
      if (!field) {
        return;
      }
      this.addRecipient(field, contact);
      if (field === "to") {
        this.toInputText = "";
      } else if (field === "cc") {
        this.ccInputText = "";
      } else {
        this.bccInputText = "";
      }
      var self = this;
      this.$nextTick(function () {
        if (field === "to" && self.$refs.toInput) {
          self.$refs.toInput.focus();
        } else if (field === "cc" && self.$refs.ccInput) {
          self.$refs.ccInput.focus();
        } else if (field === "bcc" && self.$refs.bccInput) {
          self.$refs.bccInput.focus();
        }
      });
    },
    openPicker: function (field) {
      if (!this.ccBccEnabled && field !== "to") {
        return;
      }
      this.showRecipientFields = true;
      this.pickerTargetField = field;
      if (this.ccBccEnabled && field === "cc") {
        this.showCc = true;
      }
      if (this.ccBccEnabled && field === "bcc") {
        this.showBcc = true;
      }
      this.pickerInitialSelected = this.getRecipientsByField(field).slice();
      this.loadContacts(this.getActiveRecipientSearchText());
      this.pickerVisible = true;
    },
    onPickerInsert: function (selected) {
      var field = this.pickerTargetField || "to";
      var list = this.getRecipientsByField(field);
      list.length = 0;
      for (var i = 0; i < selected.length; i++) {
        this.addRecipient(field, selected[i]);
      }
      this.pickerVisible = false;
      this.syncRecipientFieldsCollapse();
    },
    onPickerSearch: function (query) {
      this.loadContacts(query);
    },
    dedupeContactsByEmail: function (list) {
      var out = [];
      var seen = {};
      if (!list) {
        return out;
      }
      for (var i = 0; i < list.length; i++) {
        var c = list[i];
        if (!c || !c.email) {
          continue;
        }
        var key = (c.email || "").trim().toLowerCase();
        if (!key || seen[key]) {
          continue;
        }
        seen[key] = true;
        out.push(c);
      }
      return out;
    },
    async loadContacts(search) {
      this.contactsLoading = true;
      try {
        var variables = this.buildContactFilterVariables();
        if (search) {
          variables.search = search;
        }
        var res = await this.$apollo.query({
          query: gql`
            query MasterContactEmailRecipients(
              $search: String
              $entities: [String]
              $partner_type: String
              $partner_ids: [Int]
            ) {
              masterContactEmailRecipients(
                search: $search
                entities: $entities
                partner_type: $partner_type
                partner_ids: $partner_ids
              ) {
                error
                message
                data {
                  id
                  name
                  email
                  designation
                  partner_name
                }
              }
            }
          `,
          variables: variables,
          fetchPolicy: "network-only",
        });
        var payload = res.data && res.data.masterContactEmailRecipients ? res.data.masterContactEmailRecipients : null;
        if (payload && !payload.error && payload.data) {
          this.allContacts = this.dedupeContactsByEmail(payload.data);
        } else {
          this.allContacts = [];
        }
      } catch (err) {
        console.error(err);
        this.allContacts = [];
      } finally {
        this.contactsLoading = false;
      }
    },
    stripHtml: function (html) {
      if (!html) {
        return "";
      }
      return html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
    },
    sendEmail: function () {
      this.submitEmail(null);
    },
    async submitEmail(scheduledAtValue) {
      this.closeScheduleMenu();
      this.commitInputText("to");
      this.commitInputText("cc");
      this.commitInputText("bcc");

      if (this.toRecipients.length === 0) {
        this.$emit("sent", { error: true, message: "Add at least one recipient in To." });
        return;
      }
      if (!this.subject.trim()) {
        this.$emit("sent", { error: true, message: "Subject is required." });
        return;
      }
      if (!this.stripHtml(this.bodyHtml)) {
        this.$emit("sent", { error: true, message: "Email content is required." });
        return;
      }

      this.isSending = true;
      try {
        var mapRecipients = function (list) {
          var out = [];
          for (var i = 0; i < list.length; i++) {
            out.push({
              email: list[i].email,
              name: list[i].name || null,
            });
          }
          return out;
        };

        var input = {
          to: mapRecipients(this.toRecipients),
          cc: mapRecipients(this.ccRecipients),
          bcc: mapRecipients(this.bccRecipients),
          subject: this.subject.trim(),
          body_html: this.bodyHtml,
        };

        if (scheduledAtValue) {
          input.scheduled_at = this.formatScheduledAtForGraphql(scheduledAtValue);
        }

        var res = await this.$apollo.mutate({
          mutation: gql`
            mutation SendMasterContactBulkEmail($input: SendMasterContactBulkEmailInput!) {
              sendMasterContactBulkEmail(input: $input) {
                error
                message
                queued_count
                scheduled_at
              }
            }
          `,
          variables: {
            input: input,
          },
        });

        var result = res.data && res.data.sendMasterContactBulkEmail ? res.data.sendMasterContactBulkEmail : null;
        if (result && result.error) {
          this.$emit("sent", { error: true, message: result.message || "Failed to send email." });
          return;
        }

        var successMessage = result && result.message ? result.message : "Emails queued successfully.";
        if (scheduledAtValue && result && result.scheduled_at) {
          var scheduledDate = new Date(result.scheduled_at);
          if (!isNaN(scheduledDate.getTime())) {
            successMessage =
              "Send scheduled for " +
              scheduledDate.toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
              });
          }
        }

        this.$emit("sent", {
          error: false,
          message: successMessage,
          scheduled: !!scheduledAtValue,
        });
        this.closeCompose();
      } catch (err) {
        console.error(err);
        var msg = err && err.message ? err.message : "Failed to send email.";
        this.$emit("sent", { error: true, message: msg });
      } finally {
        this.isSending = false;
      }
    },
  },
};
</script>

<style scoped>
.gmail-compose {
  position: fixed;
  bottom: 0;
  right: 1.5rem;
  width: 560px;
  max-width: calc(100vw - 2rem);
  background: #fff;
  border-radius: 8px 8px 0 0;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  z-index: 110;
  display: flex;
  flex-direction: column;
  font-family: "Google Sans", Roboto, Arial, sans-serif;
}

.gmail-compose:not(.gmail-compose--min):not(.gmail-compose--fullscreen) {
  max-height: min(80vh, 720px);
}

.gmail-compose--min {
  height: auto;
  max-height: none;
}

.gmail-compose--max {
  width: min(720px, calc(100vw - 2rem));
  height: min(80vh, 720px);
}

.gmail-compose--fullscreen {
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100vw;
  max-width: 100vw;
  height: 100vh;
  max-height: 100vh;
  border-radius: 0;
  z-index: 200;
}

.gmail-compose--fullscreen .gmail-compose__header {
  border-radius: 0;
}

.gmail-compose--fullscreen .gmail-compose__recipients-wrap {
  max-height: min(32vh, 280px);
}

.gmail-compose__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  background: #404040;
  color: #fff;
  border-radius: 8px 8px 0 0;
}

.gmail-compose__header--clickable {
  cursor: pointer;
}

.gmail-compose__title {
  flex: 1;
  min-width: 0;
  font-size: 0.875rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-right: 0.5rem;
}

.gmail-compose__header-actions {
  display: flex;
  gap: 0.25rem;
}

.gmail-compose__icon-btn {
  border: none;
  background: transparent;
  color: #fff;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gmail-compose__icon-btn:hover {
  background: rgba(255, 255, 255, 0.15);
}

.gmail-compose__body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.gmail-compose__recipients-wrap {
  position: relative;
  flex-shrink: 0;
  overflow: visible;
  border-bottom: none;
  z-index: 10;
}

.gmail-compose__recipients-scroll {
  max-height: min(38vh, 240px);
  overflow-x: hidden;
  overflow-y: auto;
  border-bottom: 1px solid #e8eaed;
  -webkit-overflow-scrolling: touch;
}

.gmail-compose__row--to {
  border-bottom: none;
}

.gmail-compose__row--to-collapsed {
  align-items: center;
}

.gmail-compose__row--to-collapsed .gmail-compose__chip-input::placeholder {
  color: #5f6368;
}

.gmail-compose__recipients-wrap .gmail-compose__row {
  border-bottom: none;
}

.gmail-compose__recipients-wrap .gmail-compose__row--divider {
  border-bottom: 1px solid #e8eaed;
}

.gmail-compose__row {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.35rem 0.75rem;
  min-height: 2.25rem;
}

.gmail-compose__row--subject {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  border-bottom: 1px solid #e8eaed;
}

.gmail-compose__field-label {
  width: 2rem;
  flex-shrink: 0;
  font-size: 0.875rem;
  color: #5f6368;
  padding-top: 0.35rem;
  border: none;
  background: none;
  text-align: left;
}

.gmail-compose__field-label--clickable {
  cursor: pointer;
  border-radius: 4px;
}

.gmail-compose__field-label--clickable:hover {
  color: #1a73e8;
  text-decoration: underline;
  background: #f1f3f4;
}

.gmail-compose__cc-bcc-links {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
  padding-top: 0.25rem;
}

.gmail-compose__link-btn {
  border: none;
  background: none;
  color: #5f6368;
  font-size: 0.8125rem;
  cursor: pointer;
  padding: 0.15rem 0.35rem;
  border-radius: 4px;
}

.gmail-compose__link-btn:hover {
  color: #1a73e8;
  background: #f1f3f4;
  text-decoration: underline;
}

.gmail-compose__chips-wrap {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
}

.gmail-compose__chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: #e8f0fe;
  color: #1967d2;
  border-radius: 16px;
  padding: 0.125rem 0.5rem 0.125rem 0.625rem;
  font-size: 0.8125rem;
  max-width: 100%;
}

.gmail-compose__chip-remove {
  border: none;
  background: none;
  color: #1967d2;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  padding: 0;
}

.gmail-compose__chip-input {
  border: none;
  outline: none;
  flex: 1;
  min-width: 120px;
  font-size: 0.875rem;
  padding: 0.25rem;
}

.gmail-compose__suggestions {
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  background: #fff;
  border: 1px solid #dadce0;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  list-style: none;
  margin: 0;
  padding: 0;
  z-index: 50;
  max-height: 220px;
  overflow-y: auto;
}

.gmail-compose__suggestion {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
}

.gmail-compose__suggestion:hover {
  background: #f1f3f4;
}

.gmail-compose__suggestion-name {
  font-size: 0.875rem;
  color: #202124;
}

.gmail-compose__suggestion-email {
  font-size: 0.75rem;
  color: #5f6368;
}

.gmail-compose__subject {
  width: 100%;
  border: none;
  outline: none;
  font-size: 0.875rem;
  padding: 0.5rem 0;
}

.gmail-compose__editor {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.gmail-compose:not(.gmail-compose--max):not(.gmail-compose--min) .gmail-compose__editor {
  min-height: 160px;
}

.gmail-compose__editor :deep(.ql-toolbar) {
  flex-shrink: 0;
  border: none;
  border-bottom: 1px solid #e8eaed;
}

.gmail-compose__editor :deep(.ql-container) {
  flex: 1;
  min-height: 0;
  border: none;
  font-size: 0.875rem;
  overflow-y: auto;
}

.gmail-compose__editor :deep(.ql-editor) {
  min-height: 120px;
}

.gmail-compose__footer {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border-top: 1px solid #e8eaed;
}

.gmail-compose__send-wrap {
  display: inline-flex;
  align-items: stretch;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(26, 115, 232, 0.25);
}

.gmail-compose__send {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #1a73e8;
  color: #fff;
  border: none;
  border-radius: 0;
  padding: 0.5rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
}

.gmail-compose__send-dropdown {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  background: #1a73e8;
  color: #fff;
  border: none;
  border-left: 1px solid rgba(255, 255, 255, 1);
  cursor: pointer;
  font-size: 0.75rem;
  transition: background 0.2s ease;
}

.gmail-compose__send-dropdown--open {
  background: #1765cc;
}

.gmail-compose__send-chevron {
  display: block;
  transition: transform 0.25s ease;
  transform-origin: center center;
}

.gmail-compose__send-chevron--open {
  transform: rotate(180deg);
}

.gmail-compose__send-wrap:hover .gmail-compose__send:not(:disabled),
.gmail-compose__send-wrap:hover .gmail-compose__send-dropdown:not(:disabled) {
  background: #1765cc;
}

.gmail-compose__send:disabled,
.gmail-compose__send-dropdown:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.gmail-compose__discard {
  margin-left: auto;
  border: none;
  background: none;
  color: #5f6368;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
}

.gmail-compose__discard:hover {
  background: #f1f3f4;
  color: #202124;
}
</style>
